<template>
<v-container
    id="grid"
    fluid
    grid-list-sm
    tag="section"
  >
    <!-- Loading State -->
    <v-layout v-if="isLoading || !hasInvoiceData" row justify-center class="pa-8">
      <v-flex xs12 text-center>
        <v-progress-circular :size="70" :width="7" color="primary" indeterminate></v-progress-circular>
        <p class="mt-4 text-h6">Carregando dados da cobrança...</p>
      </v-flex>
    </v-layout>

    <!-- Error State -->
    <v-layout v-else-if="!isLoading && !hasInvoiceData" row justify-center class="pa-8">
      <v-flex xs12 text-center>
        <v-icon size="80" color="error">error_outline</v-icon>
        <p class="mt-4 text-h6">Cobrança não encontrada</p>
        <p class="text-body-1 grey--text">Verifique se o ID está correto ou tente novamente.</p>
        <v-btn color="primary" @click="reloadInvoice" class="mt-4">
          <v-icon left>refresh</v-icon>
          Tentar Novamente
        </v-btn>
      </v-flex>
    </v-layout>

    <!-- Content -->
    <v-layout v-else row wrap>
      <!-- Informações Principais -->
      <v-flex d-flex xs12 md6 order-xs5>
        <v-layout column>
          <v-flex>
            <v-card class="elevation-3 mb-4">
              <v-card-title class="primary white--text">
                <v-icon left color="white">receipt</v-icon>
                Detalhes da Cobrança
              </v-card-title>
              <v-card-text class="pa-4">
                <v-layout row wrap>
                  <v-flex xs12 sm6 class="mb-3">
                    <div class="info-item">
                      <v-icon color="primary" class="mr-2">person</v-icon>
                      <div>
                        <div class="info-label">Nome</div>
                        <div class="info-value">{{ invoice.name || 'N/A' }}</div>
                      </div>
                    </div>
                  </v-flex>
                  <v-flex xs12 sm6 class="mb-3">
                    <div class="info-item">
                      <v-icon color="primary" class="mr-2">email</v-icon>
                      <div>
                        <div class="info-label">E-mail</div>
                        <div class="info-value">{{ invoice.email || 'N/A' }}</div>
                      </div>
                    </div>
                  </v-flex>
                  <v-flex xs12 sm6 class="mb-3">
                    <div class="info-item">
                      <v-icon color="primary" class="mr-2">monetization_on</v-icon>
                      <div>
                        <div class="info-label">Valor</div>
                        <div class="info-value text-h6 primary--text">{{ invoice.amount ? $n(parseFloat(invoice.amount), 'currency') : 'N/A' }}</div>
                      </div>
                    </div>
                  </v-flex>
                  <v-flex xs12 sm6 class="mb-3">
                    <div class="info-item">
                      <v-icon color="primary" class="mr-2">event</v-icon>
                      <div>
                        <div class="info-label">Gerado em</div>
                        <div class="info-value">{{ invoice.created_at ? $d(getDate(invoice.created_at), 'dateAndTime') : 'N/A' }}</div>
                      </div>
                    </div>
                  </v-flex>
                  <v-flex xs12 sm6 class="mb-3">
                    <div class="info-item">
                      <v-icon color="primary" class="mr-2">schedule</v-icon>
                      <div>
                        <div class="info-label">Vencimento</div>
                        <div class="info-value">{{ invoice.duedate ? $d(getDate(invoice.duedate)) : 'N/A' }}</div>
                      </div>
                    </div>
                  </v-flex>
                  <v-flex xs12 sm6 class="mb-3">
                    <div class="info-item">
                      <v-icon color="primary" class="mr-2">info</v-icon>
                      <div>
                        <div class="info-label">Status</div>
                        <div class="info-value">
                          <v-chip v-if="invoice.status" :class="setClass(invoice.status)" small text-color="white">
                            {{ $t(`status.${invoice.status}`) }}
                          </v-chip>
                          <span v-else>N/A</span>
                        </div>
                      </div>
                    </div>
                  </v-flex>
                  <v-flex xs12 class="mb-3">
                    <div class="info-item">
                      <v-icon color="primary" class="mr-2">description</v-icon>
                      <div>
                        <div class="info-label">Descrição</div>
                        <div class="info-value">{{ invoice.description || 'N/A' }}</div>
                      </div>
                    </div>
                  </v-flex>
                  <v-flex xs12 v-if="invoice.installment_limit">
                    <div class="info-item">
                      <v-icon color="primary" class="mr-2">credit_card</v-icon>
                      <div>
                        <div class="info-label">Limite de Parcelas</div>
                        <div class="info-value">{{ invoice.installment_limit ? invoice.installment_limit + 'x' : 'N/A' }}</div>
                      </div>
                    </div>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-flex>

      <!-- Link de Pagamento -->
      <v-flex d-flex xs12 md6>
        <v-layout column>
          <v-flex>
            <v-card class="elevation-3 mb-4">
              <v-card-title class="success white--text">
                <v-icon left color="white">link</v-icon>
                Link de Pagamento
              </v-card-title>
              <v-card-text class="pa-4">
                <v-alert 
                  :value="true" 
                  color="info" 
                  icon="info" 
                  outline
                  class="mb-4"
                >
                  Compartilhe este link com o cliente para que ele possa efetuar o pagamento
                </v-alert>
                
                <div class="link-container mb-4">
                  <v-text-field
                    :value="paymentLink"
                    label="Link de Pagamento"
                    readonly
                    outlined
                    append-icon="content_copy"
                    @click:append="copyLink(paymentLink)"
                    class="link-input"
                  ></v-text-field>
                </div>

                <v-layout row wrap class="action-buttons">
                  <v-flex xs12 sm6 class="pr-2">
                    <v-btn 
                      color="primary" 
                      block 
                      large
                      @click="copyLink(paymentLink)"
                      :disabled="!paymentLink"
                    >
                      <v-icon left>content_copy</v-icon>
                      Copiar Link
                    </v-btn>
                  </v-flex>
                  <v-flex xs12 sm6 class="pl-2">
                    <v-btn 
                      color="success" 
                      block 
                      large
                      @click="openLink()"
                      :disabled="!paymentLink"
                    >
                      <v-icon left>open_in_new</v-icon>
                      Abrir Link
                    </v-btn>
                  </v-flex>
                  <v-flex xs12 class="mt-3">
                    <v-btn 
                      color="info" 
                      block 
                      large
                      @click="shareWhatsApp()"
                      :disabled="!paymentLink"
                    >
                      <v-icon left>message</v-icon>
                      Compartilhar via WhatsApp
                    </v-btn>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>

          <!-- QR Code (se disponível) -->
          <v-flex v-if="qrCodeAvailable">
            <v-card class="elevation-3">
              <v-card-title class="purple white--text">
                <v-icon left color="white">qr_code</v-icon>
                QR Code
              </v-card-title>
              <v-card-text class="text-center pa-4">
                <div class="qr-container" v-if="paymentLink">
                  <div id="qr-code" class="qr-code-wrapper"></div>
                  <p class="mt-3 text-body-2 grey--text">Escaneie o QR Code para acessar o link de pagamento</p>
                </div>
                <div v-else class="qr-placeholder">
                  <v-icon size="120" color="grey lighten-1">qr_code</v-icon>
                  <p class="mt-2 grey--text">Aguardando link de pagamento...</p>
                </div>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>

    <!-- Snackbar para feedback -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      bottom
    >
      {{ snackbar.message }}
      <v-btn flat @click="snackbar.show = false">
        Fechar
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<style scoped>
.info-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
}

.info-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  word-break: break-word;
}

.link-container {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
}

.link-input .v-input__slot {
  background-color: white !important;
}

.action-buttons .v-btn {
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
}

.qr-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.qr-code-wrapper {
  padding: 10px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
}

.qr-code-wrapper canvas {
  display: block;
  border-radius: 4px;
}

.qr-placeholder {
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
  border: 2px dashed #ddd;
}

.v-chip.invoice.cancelled {
  background-color: #b71c1c !important;
}
.v-chip.invoice.completed {
  background-color: #1b5e20 !important;
}
.v-chip.invoice.waiting {
  background-color: #ef6c00 !important;
}
.v-chip.invoice.approved {
  background-color: #2e7d32 !important;
}
.v-chip.invoice.failed {
  background-color: #c62828 !important;
}

@media (max-width: 600px) {
  .action-buttons .v-flex {
    padding: 4px 0 !important;
  }
  
  .action-buttons .pr-2,
  .action-buttons .pl-2 {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}
</style>

<script>
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';

export default {
  name: 'InvoiceContent',
  created() {
    this.$store.dispatch('invoices/get', this.$route.params.id);
  },
  mounted() {
    this.$nextTick(() => {
      this.generateQRCode();
    });
  },
  watch: {
    paymentLink(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          this.generateQRCode();
        });
      }
    }
  },
  computed: {
    ...mapGetters({
      invoice: 'invoices/invoice',
      isLoading: 'invoices/loadingData',
    }),
    hasInvoiceData() {
      return this.invoice && Object.keys(this.invoice).length > 0;
    },
    paymentLink() {
      if (this.hasInvoiceData && this.invoice.uuid) {
        const hostUrl = process.env.VUE_APP_HOST_URL || window.location.origin;
        return `${hostUrl}/paylink/${this.invoice.uuid}`;
      }
      return '';
    },
    qrCodeAvailable() {
      return this.hasInvoiceData && this.invoice.uuid;
    }
  },
  data() {
    return {
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    };
  },
  methods: {
    ...mapActions({
      setCurrentPage: 'invoices/setCurrentPage',
      setLimit: 'invoices/setLimit',
    }),
    setClass(status) {
      return `invoice ${status}`;
    },
    setChipColor(color) {
      return this.$t(`colors.${color}`);
    },
    getDate(date) {
      return moment(date);
    },
    copyLink(link) {
      if (!link) {
        this.showMessage('Link não disponível', 'error');
        return;
      }
      
      // Tentar usar a API moderna de clipboard primeiro
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(link).then(() => {
          this.showMessage('Link copiado com sucesso! ✅', 'success');
        }).catch(() => {
          this.fallbackCopyToClipboard(link);
        });
      } else {
        this.fallbackCopyToClipboard(link);
      }
    },
    fallbackCopyToClipboard(link) {
      const el = document.createElement('textarea');
      el.value = link;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand('copy');
        this.showMessage('Link copiado com sucesso! ✅', 'success');
      } catch (err) {
        this.showMessage('Erro ao copiar. Tente selecionar o texto manualmente.', 'error');
      }
      document.body.removeChild(el);
    },
    openLink() {
      if (this.paymentLink) {
        window.open(this.paymentLink, '_blank');
      }
    },
    shareWhatsApp() {
      if (!this.paymentLink) {
        this.showMessage('Link não disponível', 'error');
        return;
      }
      
      const message = encodeURIComponent(
        `Olá! Aqui está o link para pagamento:\n\n` +
        `💳 Valor: ${this.invoice.amount ? this.$n(parseFloat(this.invoice.amount), 'currency') : 'N/A'}\n` +
        `📅 Vencimento: ${this.invoice.duedate ? this.$d(this.getDate(this.invoice.duedate)) : 'N/A'}\n` +
        `📝 Descrição: ${this.invoice.description || 'N/A'}\n\n` +
        `🔗 Link: ${this.paymentLink}\n\n` +
        `Clique no link para efetuar o pagamento de forma segura.`
      );
      
      const whatsappUrl = `https://wa.me/?text=${message}`;
      window.open(whatsappUrl, '_blank');
    },
    showMessage(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    reloadInvoice() {
      this.$store.dispatch('invoices/get', this.$route.params.id);
    },
    generateQRCode() {
      if (!this.paymentLink) return;
      
      // Limpar QR code anterior
      const qrContainer = document.getElementById('qr-code');
      if (!qrContainer) return;
      qrContainer.innerHTML = '';
      
      // Verificar se a biblioteca qrcode está disponível
      if (typeof QRCode !== 'undefined') {
        try {
          // Criar um canvas para o QR code
          const canvas = document.createElement('canvas');
          qrContainer.appendChild(canvas);
          
          // Gerar QR code
          QRCode.toCanvas(canvas, this.paymentLink, {
            width: 200,
            height: 200,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            },
            errorCorrectionLevel: 'M'
          }, (error) => {
            if (error) {
              console.error('Erro ao gerar QR Code:', error);
              this.generateQRCodeWithService(qrContainer);
            }
          });
          return;
        } catch (error) {
          console.log('Erro ao gerar QR Code com biblioteca:', error);
        }
      }
      
      // Fallback: usar serviço online de QR Code
      this.generateQRCodeWithService(qrContainer);
    },
    generateQRCodeWithService(container) {
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(this.paymentLink)}`;
      
      const img = document.createElement('img');
      img.src = qrUrl;
      img.alt = 'QR Code do Link de Pagamento';
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.onload = () => {
        container.appendChild(img);
      };
      img.onerror = () => {
        // Se falhar, criar um placeholder visual
        this.createQRCodePlaceholder(container);
      };
    },
    createQRCodePlaceholder(container) {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 200;
      container.appendChild(canvas);
      this.drawQRCodeToCanvas(canvas, this.paymentLink);
    },
    drawQRCodeToCanvas(canvas, text) {
      // Implementação simples de QR Code usando uma biblioteca externa via CDN
      // Como fallback, vamos criar um placeholder visual melhor
      const ctx = canvas.getContext('2d');
      
      // Fundo branco
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, 200, 200);
      
      // Borda
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.strokeRect(10, 10, 180, 180);
      
      // Padrão de QR Code simples (visual apenas)
      ctx.fillStyle = '#000000';
      
      // Cantos (quadrados de orientação)
      this.drawCornerSquare(ctx, 20, 20);
      this.drawCornerSquare(ctx, 150, 20);
      this.drawCornerSquare(ctx, 20, 150);
      
      // Padrão aleatório no meio (baseado no texto)
      const hash = this.simpleHash(text);
      for (let i = 0; i < 100; i++) {
        const x = 60 + (hash * i * 17) % 80;
        const y = 60 + (hash * i * 23) % 80;
        ctx.fillRect(x, y, 4, 4);
      }
      
      // Texto pequeno embaixo
      ctx.fillStyle = '#666666';
      ctx.font = '8px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('QR Code', 100, 195);
    },
    drawCornerSquare(ctx, x, y) {
      // Quadrado externo
      ctx.fillRect(x, y, 30, 30);
      // Quadrado interno branco
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(x + 6, y + 6, 18, 18);
      // Quadrado central preto
      ctx.fillStyle = '#000000';
      ctx.fillRect(x + 12, y + 12, 6, 6);
    },
    simpleHash(str) {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Converter para 32bit integer
      }
      return Math.abs(hash);
    }
  },
};
</script>
